import type { ApiEnvironment } from 'src/shared/helpers/environment';

import type {
  CustomizationCategory,
  MaterialRegionOrPaint,
} from './customizationCategories';

export enum LeadFormFieldEnum {
  FULL_NAME = 'fullName',
  PHONE_NUMBER = 'phoneNumber',
  EMAIL = 'email',
  POSTAL_CODE = 'postalCode',
}

export enum DesignerPartner {
  PELLA = 'pella',
  REALTOR = 'realtor',
  NONE = 'none',
}

type PartnerOrgIds = { [OrgId: string]: DesignerPartner };

export const DESIGNER_PARTNER_MAP: Record<
  ApiEnvironment,
  PartnerOrgIds | null
> = {
  production: {
    '702562': DesignerPartner.PELLA, // MEOW-5511
    '651112': DesignerPartner.REALTOR,
  },
  staging: {
    '32047': DesignerPartner.PELLA, // MEOW-5511
  },
  sandbox: null,
  mock: null,
} as const;

export interface LeadFormFieldConfiguration {
  [LeadFormFieldEnum.FULL_NAME]: boolean;
  [LeadFormFieldEnum.PHONE_NUMBER]: boolean;
  [LeadFormFieldEnum.EMAIL]: boolean;
  [LeadFormFieldEnum.POSTAL_CODE]: boolean;
}

export interface DesignerPartnerConfiguration {
  brandType?: MaterialRegionOrPaint;
  defaultLeadFormConfiguration: number;
}

export const PARTNER_CONFIGURATION_MAP: Record<
  DesignerPartner,
  DesignerPartnerConfiguration
> = {
  [DesignerPartner.PELLA]: {
    brandType: 'windows',
    defaultLeadFormConfiguration: 3,
  },
  [DesignerPartner.REALTOR]: {
    defaultLeadFormConfiguration: 2,
  },
  [DesignerPartner.NONE]: {
    defaultLeadFormConfiguration: 1,
  },
};

export interface DesignerPartnerProductConfiguration {
  category: CustomizationCategory;
  productCatalogCategory: string;
}

const DEFAULT_PARTNER_PRODUCTS: DesignerPartnerProductConfiguration[] = [
  {
    // NOTE: we currently treat all paint sub-regions as the same product
    // category. See `isCategoryEqual` in `components/Customize/menu.ts`
    category: { type: 'paint', region: 'walls' },
    productCatalogCategory: '820cc7d0-3f37-44d6-a78a-538863c7a84a',
  },
  {
    category: { type: 'material', region: 'walls' },
    productCatalogCategory: 'fa089def-cc9d-4f0f-a98f-743f1a90ee28',
  },
  {
    category: { type: 'material', region: 'roof' },
    productCatalogCategory: 'b7674473-9564-4768-abe4-222c3d8138dc',
  },
  {
    category: { type: 'material', region: 'floor' },
    productCatalogCategory: 'ff3f43ec-edf7-4e60-af40-fb20c322323f',
  },
  {
    category: { type: 'material', region: 'cabinet' },
    productCatalogCategory: '4dbbd4a1-a1fc-4ab4-b2f3-361346d3b1c9',
  },
  {
    category: { type: 'material', region: 'windows' },
    productCatalogCategory: '01e7ba61-ee7c-4ff6-8af4-fe17e59894ce',
  },
];

export const PARTNER_PRODUCT_MAP = {
  [DesignerPartner.PELLA]: [
    {
      category: { type: 'material', region: 'windows' },
      productCatalogCategory: '83223cdc-fe47-4f7a-8e52-20a7bc6ff2d0',
    },
  ],
  [DesignerPartner.REALTOR]: DEFAULT_PARTNER_PRODUCTS,
  [DesignerPartner.NONE]: DEFAULT_PARTNER_PRODUCTS,
} as const satisfies Record<
  DesignerPartner,
  DesignerPartnerProductConfiguration[]
>;

export interface LeadFormConfiguration {
  hasLeadForm: boolean;
  cta?: string;
  leadFormFields: LeadFormFieldConfiguration;
}

// This is a separate lookup because a single org
// can have multiple lead form configurations
export const LEAD_FORM_CONFIGURATION_MAP: Record<
  number,
  LeadFormConfiguration
> = {
  1: {
    hasLeadForm: true,
    cta: 'Add your photo',
    leadFormFields: {
      [LeadFormFieldEnum.FULL_NAME]: true,
      [LeadFormFieldEnum.PHONE_NUMBER]: true,
      [LeadFormFieldEnum.EMAIL]: true,
      [LeadFormFieldEnum.POSTAL_CODE]: true,
    },
  },
  2: {
    hasLeadForm: true,
    cta: '',
    leadFormFields: {
      [LeadFormFieldEnum.FULL_NAME]: true,
      [LeadFormFieldEnum.PHONE_NUMBER]: true,
      [LeadFormFieldEnum.EMAIL]: true,
      [LeadFormFieldEnum.POSTAL_CODE]: true,
    },
  },
  3: {
    hasLeadForm: true,
    cta: 'Visualize windows',
    leadFormFields: {
      [LeadFormFieldEnum.FULL_NAME]: false,
      [LeadFormFieldEnum.PHONE_NUMBER]: false,
      [LeadFormFieldEnum.EMAIL]: true,
      [LeadFormFieldEnum.POSTAL_CODE]: true,
    },
  },
};
